import { StreamChatMessage } from "@/shared/types/chat";
import { assetUrl } from "@/shared/utils/assets";
import { Trash2 } from "lucide-react";

interface StreamChatFeedProps {
  messages: StreamChatMessage[];
  exitingIds: string[];
  onModerate: (message: StreamChatMessage) => void;
}

const bubbleClassName = (kind: StreamChatMessage["kind"]) => {
  switch (kind) {
    case "bad":
      return "border-red-400/42 bg-black/44 text-red-50";
    case "morale":
      return "border-fuchsia-300/30 bg-black/40 text-white";
    case "self":
      return "border-violet-300/30 bg-black/40 text-white";
    default:
      return "border-white/10 bg-black/40 text-white/92";
  }
};

const trashClassName = (kind: StreamChatMessage["kind"]) => {
  switch (kind) {
    case "bad":
      return "border-red-300/45 bg-red-400/12 text-red-100 hover:bg-red-400/22";
    case "nice":
    case "morale":
      return "border-fuchsia-300/28 bg-fuchsia-400/10 text-fuchsia-100 hover:bg-fuchsia-400/18";
    case "self":
      return "border-violet-300/28 bg-violet-400/10 text-violet-100 hover:bg-violet-400/18";
    default:
      return "border-white/12 bg-white/8 text-white/75 hover:bg-white/12";
  }
};

const EMOTE_IMAGE_BY_TOKEN: Record<string, string> = {
  ":catJam:": assetUrl("emotes/catJam.webp"),
  ":monkaS:": assetUrl("emotes/monkaS.webp"),
  ":peepoArrive:": assetUrl("emotes/peepoArrive.webp"),
  ":peepoHappy:": assetUrl("emotes/peepoHappy.webp"),
  ":peepoShy:": assetUrl("emotes/peepoShy.webp"),
  ":pepeJAM:": assetUrl("emotes/pepeJAM.webp"),
  ":pepeLaugh:": assetUrl("emotes/pepeLaugh.webp"),
  ":popCat:": assetUrl("emotes/popCat.webp"),
};

const renderChatText = (text: string) => {
  const parts = text.split(/(\s+)/);

  return parts.map((part, index) => {
    const emoteSrc = EMOTE_IMAGE_BY_TOKEN[part];

    if (!emoteSrc) {
      return <span key={`${part}-${index}`}>{part}</span>;
    }

    return (
      <img
        key={`${part}-${index}`}
        src={emoteSrc}
        alt={part}
        className="mx-0.5 inline-block h-5 w-5 rounded-sm object-cover align-[-0.2rem]"
      />
    );
  });
};

const StreamChatFeed = ({
  messages,
  exitingIds,
  onModerate,
}: StreamChatFeedProps) => {
  if (messages.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-x-3 bottom-24 z-20 flex max-h-[34%] flex-col justify-end gap-2.5 overflow-hidden sm:inset-x-4 sm:bottom-28">
      {messages.map((message) => (
        <div
          key={message.id}
          className={[
            "pointer-events-auto group flex max-w-[78%] items-end gap-2.5 transition duration-200 select-none",
            message.kind === "self"
              ? "self-end flex-row-reverse"
              : "self-start",
            exitingIds.includes(message.id)
              ? "translate-y-2 opacity-0"
              : "translate-y-0 opacity-100",
          ].join(" ")}
        >
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/15 text-[10px] font-semibold text-white shadow-[0_8px_18px_rgba(0,0,0,0.22)]"
            style={{
              backgroundImage: `linear-gradient(135deg, ${message.avatar.from}, ${message.avatar.to})`,
            }}
          >
            {message.avatar.imageSrc ? (
              <img
                src={message.avatar.imageSrc}
                alt={message.author}
                className="h-full w-full object-cover"
              />
            ) : (
              message.avatar.initials
            )}
          </div>

          <div
            className={[
              "relative min-w-0 rounded-[1.15rem] border px-3 py-2.5 shadow-[0_10px_24px_rgba(0,0,0,0.18)] backdrop-blur-xl",
              bubbleClassName(message.kind),
            ].join(" ")}
          >
            <button
              type="button"
              onClick={() => onModerate(message)}
              className={[
                "absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full border opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100",
                trashClassName(message.kind),
              ].join(" ")}
              aria-label="Remove message"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>

            <div className="min-w-0">
              <p className="truncate pr-8 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/50">
                {message.author}
              </p>
              <p className="mt-1 text-[13px] leading-5">
                {renderChatText(message.text)}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StreamChatFeed;
